import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Upload, FileSpreadsheet, ArrowLeft, X, Check, History, RefreshCw, Filter } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { pvImports } from "@/lib/api";

// Stage 3: two-step wizard — Upload → Preview → Confirm → Result.
// Uses axios directly for multipart (Authorization header auto-attached
// via the interceptor in api.js because api instance shares defaults).
const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const REJECT_REASONS = [
  "Job Card not actually pending",
  "Duplicate or incorrect Job Card",
  "Data error",
  "Vehicle already completed",
  "Other",
];

const KPI = ({ label, value, tone = "slate" }) => {
  const map = {
    slate:   "bg-[#faf8ff] text-[#1a1b22]",
    blue:    "bg-blue-50 text-blue-700",
    amber:   "bg-amber-50 text-amber-700",
    emerald: "bg-emerald-50 text-emerald-700",
    rose:    "bg-rose-50 text-rose-700",
    purple:  "bg-purple-50 text-purple-700",
  };
  return (
    <div className={`rounded-lg px-3 py-2.5 border border-[#e2e1eb] ${map[tone]}`}>
      <div className="text-[10.5px] uppercase tracking-[0.08em] font-semibold opacity-70">{label}</div>
      <div className="text-[20px] font-semibold leading-none mt-1.5">{value}</div>
    </div>
  );
};

const PvUpload = () => {
  const { currentUser, locations } = useApp();
  const navigate = useNavigate();
  const allowed = useMemo(
    () => locations.filter((l) => currentUser?.role === "admin" || currentUser?.location_codes?.includes(l.code)),
    [locations, currentUser]
  );
  const defaultLoc = allowed.length === 1 ? allowed[0].code : "";

  const [locationCode, setLocationCode] = useState(defaultLoc);
  const [reportDate, setReportDate] = useState(new Date().toISOString().slice(0, 10));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dupWarn, setDupWarn] = useState(null); // {message}

  const [preview, setPreview] = useState(null); // { import_id, summary, rows }
  const [rowStates, setRowStates] = useState({}); // jc → { selected, treatment, reason, remarks }
  const [rejectPromptFor, setRejectPromptFor] = useState(null); // list of jc numbers to reject

  const [result, setResult] = useState(null);
  const [tab, setTab] = useState("upload"); // upload | history

  const buildForm = () => {
    const fd = new FormData();
    fd.append("location_code", locationCode);
    fd.append("report_date", reportDate);
    fd.append("file", file);
    return fd;
  };

  const doPreview = async (force = false) => {
    if (!locationCode) return toast.error("Location is required.");
    if (!reportDate)   return toast.error("Report Date is required.");
    if (!file)         return toast.error("Please choose a file.");
    if (!file.name.toLowerCase().match(/\.(xls|xlsx)$/))
      return toast.error("Only XLS or XLSX files are supported.");
    setBusy(true);
    setDupWarn(null);
    try {
      const fd = buildForm();
      if (force) fd.append("force", "true");
      const token = localStorage.getItem("iva_access_token");
      const resp = await axios.post(`${API_URL}/pv/upload-preview`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = resp.data;
      if (d.duplicate_warning) {
        setDupWarn({ message: d.message });
        return;
      }
      setPreview(d);
      const state = {};
      d.rows.forEach((r) => { state[r.job_card_no] = { selected: false, treatment: "pending" }; });
      setRowStates(state);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const setRow = (jc, patch) => setRowStates((s) => ({ ...s, [jc]: { ...s[jc], ...patch } }));

  const selectAll = (checked) => {
    const next = { ...rowStates };
    preview.rows.forEach((r) => { next[r.job_card_no] = { ...next[r.job_card_no], selected: checked }; });
    setRowStates(next);
  };

  const applyBulk = (treatment) => {
    const selected = preview.rows.filter((r) => rowStates[r.job_card_no]?.selected).map((r) => r.job_card_no);
    if (selected.length === 0) return toast.error("Select at least one row.");
    if (treatment === "reject") {
      setRejectPromptFor(selected);
      return;
    }
    const next = { ...rowStates };
    selected.forEach((jc) => { next[jc] = { ...next[jc], treatment: "pending" }; });
    setRowStates(next);
    toast.success(`${selected.length} rows marked Pending Vehicle`);
  };

  const applyRejectPrompt = (reason, remarks) => {
    if (!reason) return toast.error("Rejection Reason is required.");
    if (reason === "Other" && !remarks?.trim()) return toast.error("Please enter remarks for 'Other'.");
    const next = { ...rowStates };
    rejectPromptFor.forEach((jc) => {
      next[jc] = { ...next[jc], treatment: "reject", rejection_reason: reason, rejection_remarks: remarks || null };
    });
    setRowStates(next);
    setRejectPromptFor(null);
    toast.success(`${rejectPromptFor.length} rows marked Rejected`);
  };

  const confirmImport = async () => {
    setBusy(true);
    try {
      const actions = preview.rows.map((r) => ({
        job_card_no: r.job_card_no,
        treatment: rowStates[r.job_card_no]?.treatment || "pending",
        rejection_reason: rowStates[r.job_card_no]?.rejection_reason || null,
        rejection_remarks: rowStates[r.job_card_no]?.rejection_remarks || null,
      }));
      const token = localStorage.getItem("iva_access_token");
      const resp = await axios.post(`${API_URL}/pv/upload-confirm`, {
        import_id: preview.import_id,
        actions,
        rows: preview.rows,
        report_date: reportDate,
        location_code: locationCode,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(resp.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Confirm failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setPreview(null); setRowStates({}); setResult(null); setFile(null); setDupWarn(null);
  };

  // ---- Rendering ------------------------------------------------------

  if (result) {
    return (
      <div className="max-w-3xl mx-auto py-16" data-testid="pv-upload-result">
        <div className="bg-white border border-[#e2e1eb] rounded-xl p-8">
          <div className="w-12 h-12 mx-auto rounded-lg bg-emerald-50 flex items-center justify-center">
            <Check className="w-6 h-6 text-emerald-700" />
          </div>
          <h2 className="text-[20px] font-semibold text-center mt-4 text-[#1a1b22]">Import Complete</h2>
          <p className="text-[13px] text-[#444653] mt-3 text-center max-w-lg mx-auto">
            The Job Card Status Report has been imported.
          </p>
          <div className="mt-5 grid grid-cols-5 gap-2 max-w-3xl mx-auto text-center" data-testid="pv-recon-summary">
            <ReconStat label="New Created" value={result.created ?? 0} tone="emerald" testId="recon-created" />
            <ReconStat label="Existing Updated" value={result.updated ?? 0} tone="teal" testId="recon-updated" />
            <ReconStat label="Rejected" value={result.rejected ?? 0} tone="rose" testId="recon-rejected" />
            <ReconStat label="Duplicates Prevented" value={result.duplicates_prevented ?? 0} tone="amber" testId="recon-dup" />
            <ReconStat label="Accidental Skipped" value={result.accidental_skipped ?? 0} tone="purple" testId="recon-acc-skipped" />
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3 max-w-md mx-auto">
            <button onClick={() => navigate("/pv/workbench")} data-testid="pv-result-goto-wb"
              className="px-4 py-2 rounded-lg bg-yamaha-700 hover:bg-yamaha-800 text-white text-[13px] font-semibold">
              Go to PV Workbench
            </button>
            <button onClick={reset} data-testid="pv-result-upload-more"
              className="px-4 py-2 rounded-lg border border-[#e2e1eb] text-[13px] text-[#444653] hover:bg-[#faf8ff]">
              Upload Another Report
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="max-w-3xl mx-auto py-10" data-testid="pv-upload-page">
        {/* Tabs */}
        <div className="border-b border-[#e2e1eb] flex items-center gap-1 mb-4">
          <button onClick={() => setTab("upload")} data-testid="pv-upload-tab-upload"
            className={`px-3 py-2 text-[12.5px] font-semibold border-b-2 -mb-px ${
              tab === "upload" ? "text-yamaha-700 border-yamaha-700" : "text-[#747684] border-transparent hover:text-[#1a1b22]"
            }`}>
            <span className="inline-flex items-center gap-1.5"><Upload className="w-3.5 h-3.5" /> Upload Report</span>
          </button>
          <button onClick={() => setTab("history")} data-testid="pv-upload-tab-history"
            className={`px-3 py-2 text-[12.5px] font-semibold border-b-2 -mb-px ${
              tab === "history" ? "text-yamaha-700 border-yamaha-700" : "text-[#747684] border-transparent hover:text-[#1a1b22]"
            }`}>
            <span className="inline-flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Upload History</span>
          </button>
        </div>

        {tab === "history" ? (
          <UploadHistoryTab allowed={allowed} currentUser={currentUser} />
        ) : (
        <div className="bg-white border border-[#e2e1eb] rounded-xl p-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-yamaha-50 flex items-center justify-center">
              <Upload className="w-6 h-6 text-yamaha-700" />
            </div>
            <div>
              <h1 className="text-[20px] font-semibold text-yamaha-800 leading-none tracking-tight">PV Upload</h1>
              <p className="text-[12px] text-[#747684] mt-1">Upload the daily Job Card Status Report.</p>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] font-semibold uppercase text-[#747684] block mb-1">Location *</label>
              <select
                data-testid="pv-upload-location"
                value={locationCode}
                onChange={(e) => setLocationCode(e.target.value)}
                disabled={allowed.length === 1}
                className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px] disabled:bg-[#f4f3fc]"
              >
                <option value="">— Select Location —</option>
                {allowed.map((l) => <option key={l.code} value={l.code}>{l.name} ({l.code})</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase text-[#747684] block mb-1">Report Date *</label>
              <input
                type="date"
                data-testid="pv-upload-report-date"
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
                className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="text-[11px] font-semibold uppercase text-[#747684] block mb-1">Upload Job Card Status Report *</label>
            <input
              type="file"
              data-testid="pv-upload-file"
              accept=".xls,.xlsx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-[13px] file:mr-3 file:px-3 file:py-2 file:rounded-md file:border-0 file:bg-yamaha-50 file:text-yamaha-700 file:font-semibold"
            />
            {file && <div className="mt-1 text-[11.5px] text-[#747684] flex items-center gap-1"><FileSpreadsheet className="w-3 h-3" /> {file.name}</div>}
          </div>
          <div className="text-[11px] text-[#747684] mt-4">
            Uploaded By: <b>{currentUser?.name}</b>
          </div>
          <button
            data-testid="pv-upload-submit"
            disabled={busy}
            onClick={() => doPreview(false)}
            className="mt-5 w-full px-4 py-2.5 rounded-lg bg-yamaha-700 hover:bg-yamaha-800 disabled:opacity-50 text-white text-[13px] font-semibold"
          >
            {busy ? "Processing…" : "Upload and Preview"}
          </button>
        </div>
        )}

        {dupWarn && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="pv-dup-warn">
            <div className="bg-white rounded-xl border border-[#e2e1eb] w-full max-w-md p-5">
              <h3 className="text-[15px] font-semibold text-[#1a1b22]">Duplicate Report</h3>
              <p className="text-[12.5px] text-[#444653] mt-2">{dupWarn.message} Reprocessing will update existing Job Cards and will not create duplicates.</p>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setDupWarn(null)} className="px-3 py-1.5 rounded-md border border-[#e2e1eb] text-[12px]">Cancel</button>
                <button data-testid="pv-dup-continue" onClick={() => { setDupWarn(null); doPreview(true); }}
                  className="px-3 py-1.5 rounded-md bg-yamaha-700 text-white text-[12px] font-semibold">Continue and Reprocess</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Preview screen
  const s = preview.summary;
  const selectedCount = Object.values(rowStates).filter((r) => r.selected).length;
  return (
    <div className="max-w-[1400px] mx-auto space-y-4" data-testid="pv-upload-preview">
      <div className="flex items-center gap-2">
        <button onClick={reset} className="text-[12.5px] text-yamaha-700 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back</button>
        <div className="ml-4">
          <h1 className="text-[20px] font-semibold text-yamaha-800 leading-none">Import Review</h1>
          <div className="text-[11.5px] text-[#747684] mt-0.5">{file?.name} · {locationCode} · Report Date: {reportDate}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPI label="Total in File" value={s.total_rows} />
        <KPI label="Opened" value={s.opened} tone="blue" />
        <KPI label="Under Process" value={s.under_process} tone="amber" />
        <KPI label="Accidental Skipped" value={s.accidental_skipped} tone="rose" />
        <KPI label="New" value={s.new} tone="emerald" />
        <KPI label="Existing" value={s.existing} tone="purple" />
      </div>

      <div className="bg-white border border-[#e2e1eb] rounded-xl p-3 flex flex-wrap items-center gap-2">
        <span className="text-[12px] text-[#747684]">{selectedCount} selected</span>
        <button data-testid="pv-bulk-pending" onClick={() => applyBulk("pending")}
          className="px-3 py-1.5 rounded-md border border-[#e2e1eb] text-[12px] font-semibold">Mark as Pending Vehicle</button>
        <button data-testid="pv-bulk-reject" onClick={() => applyBulk("reject")}
          className="px-3 py-1.5 rounded-md border border-rose-200 bg-rose-50 text-rose-700 text-[12px] font-semibold">Reject Selected</button>
        <div className="flex-1"></div>
        <button onClick={reset} className="px-3 py-1.5 rounded-md border border-[#e2e1eb] text-[12px]">Cancel</button>
        <button data-testid="pv-confirm-import" disabled={busy} onClick={confirmImport}
          className="px-4 py-1.5 rounded-md bg-yamaha-700 text-white text-[12px] font-semibold disabled:opacity-50">
          {busy ? "Importing…" : "Confirm Import"}
        </button>
      </div>

      <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
        <div className="overflow-x-auto max-h-[62vh]">
          <table className="w-full text-[11.5px]" data-testid="pv-preview-table">
            <thead className="bg-[#faf8ff] sticky top-0 text-[10.5px] text-[#747684] uppercase tracking-wider">
              <tr>
                <th className="px-2 py-2 text-left"><input type="checkbox" onChange={(e) => selectAll(e.target.checked)} /></th>
                <th className="px-2 py-2 text-left">JC No</th>
                <th className="px-2 py-2 text-left">JC Date</th>
                <th className="px-2 py-2 text-left">Customer</th>
                <th className="px-2 py-2 text-left">Mobile</th>
                <th className="px-2 py-2 text-left">Model</th>
                <th className="px-2 py-2 text-left">Reg No</th>
                <th className="px-2 py-2 text-left">Service Type</th>
                <th className="px-2 py-2 text-left">JC Status</th>
                <th className="px-2 py-2 text-left">Pending Reason</th>
                <th className="px-2 py-2 text-right">Pending Days</th>
                <th className="px-2 py-2 text-left">Treatment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#eeecf6]">
              {preview.rows.map((r) => {
                const st = rowStates[r.job_card_no] || {};
                return (
                  <tr key={r.job_card_no} className={st.treatment === "reject" ? "bg-rose-50/40" : ""}>
                    <td className="px-2 py-1.5"><input type="checkbox" checked={!!st.selected} onChange={(e) => setRow(r.job_card_no, { selected: e.target.checked })} /></td>
                    <td className="px-2 py-1.5 font-mono font-semibold text-yamaha-700">
                      {r.job_card_no}
                      {r.is_existing && <span className="ml-1 text-[9.5px] px-1 py-0.5 rounded bg-purple-50 text-purple-700">EXISTING</span>}
                    </td>
                    <td className="px-2 py-1.5">{r.job_card_date}</td>
                    <td className="px-2 py-1.5">{r.customer_name}</td>
                    <td className="px-2 py-1.5">{r.mobile_number}</td>
                    <td className="px-2 py-1.5">{r.vehicle_model}</td>
                    <td className="px-2 py-1.5 font-mono">{r.registration_number}</td>
                    <td className="px-2 py-1.5">{r.service_type}</td>
                    <td className="px-2 py-1.5">{r.job_card_status}</td>
                    <td className="px-2 py-1.5 max-w-[180px] truncate">{r.pending_reason}</td>
                    <td className="px-2 py-1.5 text-right">{r.pending_days ?? "—"}</td>
                    <td className="px-2 py-1.5">
                      <select
                        value={st.treatment || "pending"}
                        onChange={(e) => {
                          if (e.target.value === "reject") { setRejectPromptFor([r.job_card_no]); }
                          else                              { setRow(r.job_card_no, { treatment: "pending" }); }
                        }}
                        className="border border-[#e2e1eb] rounded px-1 py-0.5 text-[11px]"
                      >
                        <option value="pending">Pending Vehicle</option>
                        <option value="reject">Reject / Do Not Import</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {rejectPromptFor && (
        <RejectPrompt onCancel={() => setRejectPromptFor(null)} onSubmit={applyRejectPrompt} count={rejectPromptFor.length} />
      )}
    </div>
  );
};

const RejectPrompt = ({ onCancel, onSubmit, count }) => {
  const [reason, setReason] = useState("Job Card not actually pending");
  const [remarks, setRemarks] = useState("");
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="pv-reject-prompt">
      <div className="bg-white rounded-xl border border-[#e2e1eb] w-full max-w-md p-5">
        <div className="flex items-center gap-2">
          <h3 className="text-[15px] font-semibold flex-1">Reject {count} Job Card{count === 1 ? "" : "s"}</h3>
          <button onClick={onCancel}><X className="w-4 h-4 text-[#747684]" /></button>
        </div>
        <label className="text-[11px] font-semibold uppercase text-[#747684] block mt-3 mb-1">Rejection Reason *</label>
        <select data-testid="pv-reject-reason" value={reason} onChange={(e) => setReason(e.target.value)}
          className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]">
          {REJECT_REASONS.map((r) => <option key={r}>{r}</option>)}
        </select>
        {reason === "Other" && (
          <>
            <label className="text-[11px] font-semibold uppercase text-[#747684] block mt-3 mb-1">Remarks *</label>
            <textarea data-testid="pv-reject-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3}
              className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" />
          </>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-1.5 rounded-md border border-[#e2e1eb] text-[12px]">Cancel</button>
          <button data-testid="pv-reject-submit" onClick={() => onSubmit(reason, remarks)}
            className="px-3 py-1.5 rounded-md bg-rose-600 text-white text-[12px] font-semibold">Reject</button>
        </div>
      </div>
    </div>
  );
};

export default PvUpload;

// ==================== Upload History Tab ====================
const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return iso;
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yy = dt.getFullYear();
    let h = dt.getHours();
    const mn = String(dt.getMinutes()).padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${dd}/${mm}/${yy} ${String(h).padStart(2, "0")}:${mn} ${ampm}`;
  } catch { return iso; }
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
};

const UploadHistoryTab = ({ allowed, currentUser }) => {
  const [pLoc, setPLoc] = useState("");
  const [pDate, setPDate] = useState("");
  const [aLoc, setALoc] = useState("");
  const [aDate, setADate] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await pvImports({ location_code: aLoc || undefined, report_date: aDate || undefined });
      setRows(d.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load upload history");
    } finally { setLoading(false); }
  }, [aLoc, aDate]);

  useEffect(() => { load(); }, [load]);

  const apply = () => { setALoc(pLoc); setADate(pDate); };
  const clear = () => { setPLoc(""); setPDate(""); setALoc(""); setADate(""); };

  return (
    <div className="space-y-3" data-testid="pv-upload-history">
      <div className="bg-white border border-[#e2e1eb] rounded-xl p-3 flex flex-wrap items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-[#747684]" />
        <select data-testid="pv-hist-loc" value={pLoc} onChange={(e) => setPLoc(e.target.value)}
          className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]">
          <option value="">All Locations</option>
          {allowed.map((l) => <option key={l.code} value={l.code}>{l.name} ({l.code})</option>)}
        </select>
        <input type="date" data-testid="pv-hist-date" value={pDate} onChange={(e) => setPDate(e.target.value)}
          className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]" />
        <button data-testid="pv-hist-apply" onClick={apply}
          className="px-3 py-1.5 rounded-md bg-yamaha-700 hover:bg-yamaha-800 text-white text-[11.5px] font-semibold">Apply</button>
        <button data-testid="pv-hist-clear" onClick={clear}
          className="px-3 py-1.5 rounded-md border border-[#e2e1eb] text-[11.5px] font-semibold text-[#444653] hover:bg-[#faf8ff]">Clear</button>
        <button data-testid="pv-hist-refresh" onClick={load}
          className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#e2e1eb] text-[11.5px] font-semibold text-[#444653] hover:bg-[#faf8ff]">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="pv-hist-table">
            <thead className="bg-[#faf8ff] text-[10.5px] text-[#747684] uppercase tracking-wider sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left">Upload Date & Time</th>
                <th className="px-3 py-2 text-left">Report Date</th>
                <th className="px-3 py-2 text-left">Location</th>
                <th className="px-3 py-2 text-left">File Name</th>
                <th className="px-3 py-2 text-left">Uploaded By</th>
                <th className="px-3 py-2 text-center">Total Rows</th>
                <th className="px-3 py-2 text-center">New</th>
                <th className="px-3 py-2 text-center">Updated</th>
                <th className="px-3 py-2 text-center">Rejected</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#eeecf6]">
              {rows.length === 0 && (
                <tr><td colSpan={10} className="text-center text-[12.5px] text-[#747684] py-10">
                  {loading ? "Loading upload history…" : "No uploads for the selected filters."}
                </td></tr>
              )}
              {rows.map((r) => {
                const status = r.import_status || "unknown";
                const isCompleted = status === "completed";
                const isFailed = status === "failed" || status === "previewed";
                return (
                  <tr key={r.id} data-testid={`pv-hist-row-${r.id}`} className="hover:bg-[#faf8ff]">
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDT(r.uploaded_at)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.report_date)}</td>
                    <td className="px-3 py-2 font-mono">{r.location_code}</td>
                    <td className="px-3 py-2 max-w-[240px] truncate" title={r.file_name}>{r.file_name || "—"}</td>
                    <td className="px-3 py-2 text-[#444653]">{r.uploaded_by || "—"}</td>
                    <td className="px-3 py-2 text-center">{r.total_rows ?? 0}</td>
                    <td className="px-3 py-2 text-center text-emerald-700 font-semibold">{r.new_records_created ?? 0}</td>
                    <td className="px-3 py-2 text-center text-teal-700">{r.existing_records_updated ?? 0}</td>
                    <td className="px-3 py-2 text-center text-rose-700">{r.rejected_records ?? 0}</td>
                    <td className="px-3 py-2">
                      {isCompleted && (
                        <span className="inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Completed
                        </span>
                      )}
                      {isFailed && (
                        <span className="inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-rose-50 text-rose-700 border border-rose-200"
                          title={r.error_reason || "Not confirmed / failed"}>
                          Failed
                        </span>
                      )}
                      {!isCompleted && !isFailed && (
                        <span className="inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-[#faf8ff] text-[#444653] border border-[#e2e1eb]">
                          {status}
                        </span>
                      )}
                      {isFailed && r.error_reason && (
                        <div className="text-[10.5px] text-rose-700 mt-1 max-w-[220px] truncate" title={r.error_reason}>
                          {r.error_reason}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ReconStat = ({ label, value, tone = "purple", testId }) => {
  const tones = {
    purple:  "bg-purple-50 border-purple-200 text-purple-700",
    rose:    "bg-rose-50 border-rose-200 text-rose-700",
    amber:   "bg-amber-50 border-amber-200 text-amber-700",
    teal:    "bg-teal-50 border-teal-200 text-teal-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  };
  return (
    <div className={`border rounded-lg py-2.5 px-2 ${tones[tone] || tones.purple}`} data-testid={testId}>
      <div className="text-[18px] font-semibold leading-none">{value}</div>
      <div className="text-[10.5px] mt-1">{label}</div>
    </div>
  );
};
